/**
 * RoutineChatPanel — the left pane of the routine split view (P12 §6.1, T-FE3).
 *
 * Reuses the existing chat UI verbatim (`MessageList` + `MessageInput`), mirroring
 * `DashboardChatSidebar`. The user describes a routine here; RoutineBuilder grounds it and fills the
 * read-only editor on the right via live `routine_draft` patches. Empty state shows starter chips
 * ({@link RoutineChatEmptyState}).
 */
import type { SSEMessage } from '@/_shared/types';

import { MessageInput } from '../../chat/components/MessageInput';
import { MessageList } from '../../chat/components/MessageList';
import { useChatFeedback } from '../../chat/hooks/useChatFeedback';
import { useRefreshChatFeedbackOnIdle } from '../../chat/hooks/useRefreshChatFeedbackOnIdle';
import { RoutineChatEmptyState } from './RoutineChatEmptyState';

interface RoutineChatPanelProps {
  /** Current routine-chat session; feedback is scoped to it. */
  sessionId: string | null;
  messages: SSEMessage[];
  agentActive: boolean;
  /** True when a confirmed turn ID is set — gates the Stop button. */
  canStop?: boolean;
  stopping?: boolean;
  stopStatusError?: boolean;
  stopStalled?: boolean;
  onRetryStopStatus?: () => void;
  /** Disable input until the session is ready. */
  disabled?: boolean;
  onSend: (content: string) => void;
  onStop?: () => void;
  prefillValue?: string;
  prefillNonce?: number;
}

export function RoutineChatPanel({
  sessionId,
  messages,
  agentActive,
  canStop = false,
  stopping = false,
  stopStatusError = false,
  stopStalled = false,
  onRetryStopStatus,
  disabled = false,
  onSend,
  onStop,
  prefillValue,
  prefillNonce,
}: RoutineChatPanelProps): React.ReactElement {
  const hasMessages = messages.length > 0;
  const feedback = useChatFeedback(sessionId);
  useRefreshChatFeedbackOnIdle(sessionId, agentActive, feedback.refresh);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Show the message list (with its thinking indicator) whenever there are messages OR the agent
          is active — so a turn that's running before its first message streams in still surfaces a
          visible "Thinking…" state instead of a blank panel with a silently-disabled input. The
          starter empty-state only shows when the pane is genuinely idle. */}
      {hasMessages || agentActive ? (
        <MessageList
          messages={messages}
          suggestionSurface="routine"
          thinking={agentActive}
          onAction={onSend}
          compact
          actionsDisabled={agentActive}
          feedback={{
            canRate: feedback.canRate,
            eligibleTurnIds: feedback.eligibleTurnIds,
            ratingByTurn: feedback.ratingByTurn,
            isPending: feedback.isPending,
            onRate: feedback.setRating,
          }}
        />
      ) : (
        <RoutineChatEmptyState disabled={disabled} onSend={onSend} />
      )}

      {/* Cap the composer at max-w-4xl and center it so it lines up with the transcript column
          (MessageList compact) — this pane is wider than a sidebar, so an uncapped input would sprawl. */}
      <div className="mx-auto w-full max-w-4xl">
        <MessageInput
          onSend={onSend}
          disabled={agentActive || disabled}
          stopping={stopping}
          stopStatusError={stopStatusError}
          stopStalled={stopStalled}
          onRetryStopStatus={onRetryStopStatus}
          variant="sidebar"
          placeholder="Describe the routine to build…"
          onStop={canStop ? onStop : undefined}
          prefillValue={prefillValue}
          prefillNonce={prefillNonce}
        />
      </div>
    </div>
  );
}
